import HomeIcon from "@mui/icons-material/Home";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ThreeDRotationIcon from "@mui/icons-material/ThreeDRotation";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Box, Fab, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, Typography } from "@mui/material";
import { bbox } from "@turf/turf";
import HTMLReactParser from "html-react-parser";
import "maplibre-gl/dist/maplibre-gl.css";
import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import Map, { Popup, Marker } from "react-map-gl/maplibre";
import ToggleButton from "../common/ToggleButton";
import { useConfig } from "../../contexts/ConfigContext";

import { useLayers } from "../../contexts/LayerContext";
// Media fetching is now handled automatically by SelectionContext.ensureCompleteEntityData
import { useCurrentPopup } from "../../contexts/PopupContext";
import { SelectionContext } from "../../contexts/SelectionContext";
import { useUser } from "../../contexts/UserContext";
import { useUserInterface } from "../../contexts/UserInterfaceContext";
import { useMapLibreLayers } from "../../hooks/useMapLibreLayers";
import { useMapFlying } from "../../hooks/useMapFlying";
import { useSnackbar } from "notistack";
import {
  MapLayerConfig,
  RasterDemLayerConfig,
  SearchResult,
  ApiEndpoint
} from "../../types";
import { searchEndpoint, enhanceMapClickData, extractFeatureData } from "../../utilities/dataSourceAdapters";
import {
  generateSearchPlaceholder,
  processSearchQuery,
  SearchablePlaceItem
} from "../../utilities/mapSearchUtils";
import {
  findAndZoomToPlaceById,
  zoomToCoordinates,
  smartZoomToLocation
} from "../../utilities/mapZoomUtils";
import SearchBar from "../common/SearchBar";
import ErrorBoundary from "../common/ErrorBoundary";
import Layers from "./Layers";
import "./MapComponent.css";
import DataItemDetails from "../data-items/DataItemDetails";

interface MapComponentProps {
  isSidePanelOpen: boolean;
}

const MapComponent: React.FC<MapComponentProps> = ({ isSidePanelOpen }) => {
  const { layers } = useLayers();
  const { profile } = useUser();
  const config = useConfig();
  const mapRef = useRef<any>(null);
  const { popupContent, setPopupContent, popupCoords, setPopupCoords, clearPopup } =
    useCurrentPopup();
  const { mapLibreLayers } = useMapLibreLayers();
  const { terrainLayers, visibleLayers } = useLayers();
  const { 
    selectedEntity,
    setSelectedEntity
  } = useContext(SelectionContext);
  
  // Use UserInterface context for layout coordination and popup visibility
  const { setSidePanelOpen, toggleDataDrawer, resetClosePopupsFlag, resetZoomToHomeFlag, state: { dataDrawerMode, shouldClosePopups, shouldZoomToHome } } = useUserInterface();
  
  // Use custom hook for map flying operations (automatically handles popup closure)
  const { flyTo, fitBounds } = useMapFlying(mapRef);

  // Sync side panel state with UserInterface context - memoized to prevent unnecessary re-renders
  useEffect(() => {
    setSidePanelOpen(isSidePanelOpen);
  }, [isSidePanelOpen, setSidePanelOpen]);

  // Effect: Listen for popup closure trigger and close all popups
  useEffect(() => {
    if (shouldClosePopups) {
      console.log('[MapComponent] Closing all popups due to trigger');
      
      // Close React popup
      setShowReactPopup(false);
      setPopupData(null);
      
      // Close MapLibre popup
      clearPopup();
      
      // Reset the flag
      resetClosePopupsFlag();
    }
  }, [shouldClosePopups, clearPopup, resetClosePopupsFlag]);

  // Effect: Listen for zoom to home trigger and zoom to initial view
  useEffect(() => {
    if (shouldZoomToHome) {
      console.log('[MapComponent] Zooming to home due to trigger');
      
      // Use the existing home click handler logic
      handleHomeClick();
      
      // Reset the flag
      resetZoomToHomeFlag();
    }
  }, [shouldZoomToHome, resetZoomToHomeFlag]);


  const [isOrbiting, setIsOrbiting] = useState(false);
  const rotationIntervalRef = useRef<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchableItemsData, setSearchableItemsData] = useState<Record<string, SearchablePlaceItem[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [serverSearchLoading, setServerSearchLoading] = useState(false);

  // Add geolocation state
  const [userLocation, setUserLocation] = useState<{
    latitude: number;
    longitude: number;
    accuracy: number;
  } | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [isTrackingLocation, setIsTrackingLocation] = useState(false);
  const locationWatchIdRef = useRef<number | null>(null);

  // Add a ref to keep track of the last processed entity to prevent loops
  const lastProcessedEntityRef = useRef<string | null>(null);
  
  // Add a ref to track coordinates/geometry to detect changes within the same entity
  const lastProcessedCoordinatesRef = useRef<string | null>(null);

  // Right-click context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    coordinates: [number, number];
  } | null>(null);
  
  const { enqueueSnackbar } = useSnackbar();

  // React popup state for DataItemDetails
  const [showReactPopup, setShowReactPopup] = useState(false);
  const [popupData, setPopupData] = useState<{
    entityId: string | number;
    entityName: string;
    endpoint: ApiEndpoint;
    coordinates: [number, number];
  } | null>(null);

  // Helper function to extract coordinates from entity (DRY principle)
  const extractEntityCoordinates = useCallback((entity: any): [number, number] => {
    if (entity.properties?.coordinates) {
      return entity.properties.coordinates;
    }
    if (entity.properties?.geometry?.coordinates) {
      return entity.properties.geometry.coordinates;
    }
    if (entity.properties?.longitude && entity.properties?.latitude) {
      return [entity.properties.longitude, entity.properties.latitude];
    }
    return [0, 0];
  }, []);

  // Helper function to find endpoint for entity (DRY principle)
  const findEndpointForEntity = useCallback((entity: any) => {
    // Direct matching without circular dependency
    let endpoint = config.apiEndpoints.find(ep => ep.tableName === entity.entityType);
    
    if (!endpoint) {
      endpoint = config.apiEndpoints.find(ep => 
        ep.name.toLowerCase() === entity.entityType.toLowerCase()
      );
    }
    
    if (!endpoint && entity.properties?.form_id) {
      endpoint = config.apiEndpoints.find(ep => ep.formId === entity.properties.form_id);
    }

    // Try layerId matching as fallback
    if (!endpoint && entity.layerId) {
      endpoint = config.apiEndpoints.find(ep => ep.mapLayerId === entity.layerId);
    }

    return endpoint || null;
  }, [config.apiEndpoints]);

  // Effect: Manage popup display based on selectedEntity and DataDrawer state
  useEffect(() => {
    console.log('[MAP-POPUP-DEBUG] Effect triggered:', {
      hasSelectedEntity: !!selectedEntity,
      selectedEntityId: selectedEntity?.id,
      dataDrawerMode,
      currentPopupEntityId: popupData?.entityId,
      showReactPopup
    });

    // Hide popup when DataDrawer is open or no entity is selected
    if (dataDrawerMode !== 'hidden' || !selectedEntity) {
      if (showReactPopup) {
        console.log('[MAP-POPUP-DEBUG] Hiding popup:', 
          dataDrawerMode !== 'hidden' ? 'DataDrawer is open' : 'No entity selected');
        setShowReactPopup(false);
        setPopupData(null);
      }
      return;
    }

    // Show/update popup when DataDrawer is hidden and we have a selected entity
    if (selectedEntity.properties && popupData?.entityId !== selectedEntity.id) {
      console.log('[MAP-POPUP-DEBUG] Updating popup for new entity:', {
        newEntityId: selectedEntity.id,
        previousEntityId: popupData?.entityId
      });
      
      const coordinates = extractEntityCoordinates(selectedEntity);
      const endpoint = findEndpointForEntity(selectedEntity);

      if (endpoint) {
        console.log('[MAP-POPUP-DEBUG] Setting new popup data');
        setPopupData({
          entityId: selectedEntity.id,
          entityName: selectedEntity.name,
          endpoint: endpoint,
          coordinates: coordinates
        });
        setShowReactPopup(true);
        clearPopup();
      } else {
        console.warn('[MAP-POPUP-DEBUG] No endpoint found, hiding popup');
        setShowReactPopup(false);
        setPopupData(null);
      }
    }
  }, [selectedEntity?.id, dataDrawerMode]); // Simplified dependencies to prevent multiple runs

  // Reactive popup component that gets complete data from SelectionContext
  const ReactivePopupContent: React.FC<{
    entityId: string | number;
    entityName: string;
    endpoint: ApiEndpoint;
    onBack: () => void;
  }> = ({ entityId, entityName, endpoint, onBack }) => {
    const { selectedEntity } = useContext(SelectionContext);
    
    // Derive popup item from SelectionContext (same logic as DataDrawer)
    const popupItem = useMemo(() => {
      if (!selectedEntity?.properties || selectedEntity.id !== entityId) {
        return null;
      }
      
      // Create complete item with relationships (same logic as DataDrawer)
      const { relationships: propsRelationships, relatedMedia: propsRelatedMedia, isComplete: propsIsComplete, ...basicProperties } = selectedEntity.properties;
      
      const completeItem = {
        ...basicProperties,
        // Include relationships from the top-level selectedEntity (where SelectionContext stores them)
        relationships: selectedEntity.relationships,
        // Include related media for consistency
        relatedMedia: selectedEntity.relatedMedia
      } as any;
      
      console.log('[ReactivePopupContent] Derived item from SelectionContext:', {
        entityId: selectedEntity.id,
        entityName: selectedEntity.name,
        hasRelationships: !!selectedEntity.relationships,
        hasRelatedMedia: !!selectedEntity.relatedMedia,
        isComplete: selectedEntity.isComplete,
        basicPropertiesKeys: Object.keys(basicProperties),
        relationshipKeys: selectedEntity.relationships ? Object.keys(selectedEntity.relationships) : []
      });
      
      return completeItem;
    }, [selectedEntity?.properties, selectedEntity?.id, selectedEntity?.relationships, selectedEntity?.relatedMedia, selectedEntity?.isComplete, entityId]);

    // Show loading state while waiting for complete data
    if (!popupItem) {
      return (
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          minWidth: 200,
          justifyContent: 'center'
        }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading {entityName}...
          </Typography>
        </Box>
      );
    }

    return (
      <DataItemDetails
        item={popupItem}
        onBack={onBack}
        dataDrawerEndpoint={endpoint}
        dataItemEndpoint={endpoint}
        availableHeight="auto"
        popupMode={true}
      />
    );
  };

  const handleHomeClick = () => {
    if (mapRef.current) {
      const initialView = config.initialMapView;

      // Use the centralized flying method (automatically closes popups)
      flyTo({
        center: [initialView.longitude, initialView.latitude],
        zoom: initialView.zoom,
        pitch: initialView.pitch,
        bearing: 0, // Reset bearing to north
        duration: 2000, // 2 seconds animation
      });

      // Note: 3D mode state is managed by the pitch value itself

      // Stop orbiting if it's active
      if (isOrbiting) {
        toggleOrbit();
      }
    }
  };

  const handleDataDrawerToggle = () => {
    toggleDataDrawer(1); // Pass Map tab index to track user intent
  };

  // Determine FAB appearance based on DataDrawer state
  const getDataDrawerFabProps = () => {
    switch (dataDrawerMode) {
      case 'compact':
        return {
          color: 'secondary' as const,
          'aria-label': 'close-data-drawer',
          title: 'Close Data Drawer'
        };
      case 'fullscreen':
        return {
          color: 'primary' as const,
          'aria-label': 'minimize-data-drawer',
          title: 'Switch to Compact View'
        };
      default: // hidden
        return {
          color: 'primary' as const,
          'aria-label': 'open-data-drawer',
          title: 'Open Data Drawer (Compact)'
        };
    }
  };

  const fabProps = getDataDrawerFabProps();
  
  // Extract title for Tooltip
  const { title: tooltipTitle } = fabProps;

  const getInteractiveLayerIds = (layers: MapLayerConfig[]): string[] => {
    return layers
      .filter((layer) => !layer.disableClicking)
      .map((layer) => layer.id);
  };

  const addMissingImages = useCallback((map: any) => {
    // Use a ref to track if this function has already been called for this map
    if (map._culturemapImageHandlerAdded) {
      return () => {}; // Return empty cleanup function if already added
    }
    
    map._culturemapImageHandlerAdded = true;
    const loadingImages = new Set();

    // Override the default console warning for styleimagemissing
    const originalWarn = console.warn;
    console.warn = function (...args) {
      if (args[0]?.includes?.("could not be loaded")) {
        return; // Suppress the specific image loading warnings
      }
      originalWarn.apply(console, args);
    };

    const handleStyleImageMissing = (e: any) => {
      const id = e.id;

      // Check if the image is already being loaded
      if (loadingImages.has(id)) {
        return;
      }

      // Double-check if the image has already been added to the map
      if (!map.hasImage(id)) {
        // Mark the image as being loaded
        loadingImages.add(id);

        // Fetch the image (you might want to adjust the path)
        fetch(id)
          .then((response) => response.blob())
          .then((blob) => createImageBitmap(blob))
          .then((imageBitmap) => {
            // Double-check again before adding (race condition protection)
            if (!map.hasImage(id)) {
              try {
                map.addImage(id, imageBitmap);
                console.log(`[MapComponent] Successfully added image: ${id}`);
              } catch (error: any) {
                if (error.message?.includes('already exists')) {
                  console.log(`[MapComponent] Image ${id} already exists, skipping`);
                } else {
                  console.warn(`[MapComponent] Error adding image ${id}:`, error);
                }
              }
            }
            loadingImages.delete(id);
          })
          .catch((error) => {
            // Quietly handle the error
            console.warn(`[MapComponent] Failed to load image ${id}:`, error);
            loadingImages.delete(id);
          });
      }
    };

    map.on("styleimagemissing", handleStyleImageMissing);

    // Cleanup when component unmounts
    return () => {
      console.warn = originalWarn; // Restore original console.warn
      map.off("styleimagemissing", handleStyleImageMissing);
      map._culturemapImageHandlerAdded = false;
    };
  }, []);

  // Helper function to find endpoint by layerId for consistent entityType
  const findEndpointByLayerId = (layerId: string) => {
    console.log('[MapComponent] Finding endpoint for layerId:', layerId);
    
    if (!config?.apiEndpoints) {
      console.log('[MapComponent] No apiEndpoints in config');
      return null;
    }
    
    console.log('[MapComponent] Available endpoints for search:', config.apiEndpoints.map(ep => ({
      name: ep.name,
      tableName: ep.tableName,
      mapLayerId: ep.mapLayerId,
      dataSourceType: ep.dataSourceType,
      formId: ep.formId
    })));
    
    // Try to find endpoint by mapLayerId first (most specific)
    let endpoint = config.apiEndpoints.find(ep => ep.mapLayerId === layerId);
    if (endpoint) {
      return endpoint;
    }
    
    // Then try by tableName (case-insensitive)
    endpoint = config.apiEndpoints.find(ep => 
      ep.tableName.toLowerCase() === layerId.toLowerCase()
    );
    if (endpoint) {
      return endpoint;
    }
    
    // Finally try by name (case-insensitive)
    endpoint = config.apiEndpoints.find(ep => 
      ep.name.toLowerCase() === layerId.toLowerCase()
    );
    if (endpoint) {
      return endpoint;
    }
    
    console.log('[MapComponent] ❌ No endpoint found for layerId:', layerId, {
      triedMatches: {
        mapLayerId: config.apiEndpoints.filter(ep => ep.mapLayerId === layerId).length,
        tableName: config.apiEndpoints.filter(ep => ep.tableName.toLowerCase() === layerId.toLowerCase()).length,
        name: config.apiEndpoints.filter(ep => ep.name.toLowerCase() === layerId.toLowerCase()).length
      },
      availableEndpoints: config.apiEndpoints.map(ep => ({
        name: ep.name,
        tableName: ep.tableName,
        mapLayerId: ep.mapLayerId,
        dataSourceType: ep.dataSourceType
      }))
    });
    
    return null;
  };

  const handleClick = async (e: any) => {
    if (!e.features || e.features.length === 0) return;
    
    // Get the clicked feature
    const feature = e.features[0];
    const layerId = feature.layer.id;
    const layerConfig = layers.find(layer => layer.id === layerId) || { joinedData: [] };
    
    console.log('[MapComponent] Map click detected:', {
      layerId,
      featureId: feature.properties.id,
      featureName: feature.properties.name,
      availableEndpoints: config?.apiEndpoints?.map(ep => ({
        name: ep.name,
        tableName: ep.tableName,
        mapLayerId: ep.mapLayerId
      })) || []
    });
    
    // Find the correct endpoint to get consistent entityType
    const endpoint = findEndpointByLayerId(layerId);
    
    console.log('[MapComponent] Endpoint resolution result:', {
      layerId,
      foundEndpoint: endpoint ? {
        name: endpoint.name,
        tableName: endpoint.tableName,
        mapLayerId: endpoint.mapLayerId,
        dataSourceType: endpoint.dataSourceType
      } : null
    });
    
    let geoEntity;
    try {
      // Let the adapter handle all data source-specific logic
      geoEntity = await enhanceMapClickData(feature, layerId, endpoint || undefined, config);
      
      console.log('[MapComponent] Enhanced geo entity created:', {
        id: geoEntity.id,
        name: geoEntity.name,
        entityType: geoEntity.entityType,
        hasCompleteData: geoEntity.properties?.hasCompleteData,
        extractionMethod: 'adapter-based'
      });
    } catch (error) {
      console.error('[MapComponent] Error enhancing map click data:', error);
      
      // Fallback to basic entity creation
      geoEntity = {
        id: feature.properties?.id || feature.properties?.name || 'unknown',
        name: feature.properties?.name || `${layerId} Feature`,
        entityType: endpoint?.tableName || layerId,
        layerId: layerId,
        coordinates: feature.geometry?.type === "Point" ? feature.geometry.coordinates as [number, number] : undefined,
        geometry: feature.geometry,
        properties: {
          ...feature.properties,
          fromMapClick: true,
          hasCompleteData: false
        }
      };
    }

    // Update SelectionContext IMMEDIATELY before starting zoom
    // This ensures DataItemDetails updates instantly when map items are clicked
    console.log('[MapComponent] Updating SelectionContext with:', {
      entityId: geoEntity.id,
      entityName: geoEntity.name,
      entityType: geoEntity.entityType,
      layerId: geoEntity.layerId,
      hasJoinedData: !!layerConfig.joinedData,
      hasCompleteData: geoEntity.properties?.hasCompleteData,
      endpointToPass: endpoint ? {
        name: endpoint.name,
        tableName: endpoint.tableName
      } : null
    });
    
    // Always use setSelectedEntity - it will automatically fetch complete data including media and relationships
    console.log(`🗺️ [MapComponent] Map click triggering 3-level adapter system:`, {
      entityId: geoEntity.id,
      entityName: geoEntity.name,
      endpointName: endpoint?.name,
      dataSourceType: endpoint?.dataSourceType,
      hasCompleteData: geoEntity.properties?.hasCompleteData,
      willTriggerLevel: geoEntity.properties?.hasCompleteData ? 'Already complete' : 'Level 2 fetch'
    });
    setSelectedEntity(geoEntity, endpoint || undefined);

    // Decide whether to show React popup or HTML popup
    // Only show popup if DataDrawer is hidden - prevents dual DataItemDetails instances
    // This architectural decision supports future bookmarkable state management
    if (dataDrawerMode === 'hidden' && endpoint) {
      // Show React popup and let it reactively use SelectionContext data
      // This ensures popup gets complete data just like DataDrawer does
      setPopupData({
        entityId: geoEntity.id,
        entityName: geoEntity.name,
        endpoint: endpoint,
        coordinates: e.lngLat ? [e.lngLat.lng, e.lngLat.lat] : [0, 0]
      });
      setShowReactPopup(true);
      // Clear any existing HTML popup
      clearPopup();
    } else {
      // Show traditional HTML popup (only if DataDrawer is hidden)
      setShowReactPopup(false);
      setPopupData(null);
      
      // Handle popup display if enabled and DataDrawer is hidden (after selection is set)
      if (dataDrawerMode === 'hidden' && !('disablePopups' in layerConfig && layerConfig.disablePopups)) {
        setPopupContent(feature.properties, layerId);
        setPopupCoords({
          lng: String(e.lngLat.lng),
          lat: String(Math.max(-90, Math.min(90, e.lngLat.lat + (feature.geometry.type === "Point" ? 0.001 : 0))))
        });
      }
    }

    // Handle zoom for any feature with geometry using centralized smart zoom
    if (feature.geometry) {
      const map = mapRef.current?.getMap();
        
      if (map) {
        try {
          // Calculate center coordinates for zoom
          const boundingBox = bbox(feature);
          const center: [number, number] = [(boundingBox[0] + boundingBox[2]) / 2, (boundingBox[1] + boundingBox[3]) / 2];
          
          // Use centralized smart zoom logic
          smartZoomToLocation({
            center,
            geometry: feature.geometry,
            map,
            duration: 3000, // Slightly longer for map clicks
            defaultZoom: 14,
            fromMapClick: true // Don't close popups for map clicks
          });
        } catch (error) {
          console.warn('Could not calculate bounds for geometry:', feature.geometry, error);
          // Selection was already set above, so just continue without zooming
        }
        return;
      }
    }
  };

  const terrainLayer = terrainLayers[0] as RasterDemLayerConfig; // Choose the first terrain layer from any provided

  const handle3DToggle = () => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      const newPitch = map.getPitch() === 0 ? 60 : 0;
      // Use regular easeTo for 3D toggle (don't close popups - it's just a view change)
      map.easeTo({ pitch: newPitch });
    }
  };

  const toggleOrbit = () => {
    if (isOrbiting) {
      // Stop orbiting
      if (rotationIntervalRef.current) {
        window.clearInterval(rotationIntervalRef.current);
        rotationIntervalRef.current = null;
      }
    } else {
      // Start orbiting
      if (mapRef.current) {
        const map = mapRef.current.getMap();
        rotationIntervalRef.current = window.setInterval(() => {
          const currentBearing = map.getBearing();
          map.easeTo({
            bearing: currentBearing + 0.5,
            duration: 50,
            easing: (t: number) => t,
          });
        }, 50);
      }
    }
    setIsOrbiting(!isOrbiting);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    
    // Use the utility function to process the search query
    const results = processSearchQuery(value, searchableItemsData, layers);
    
    setSearchResults(results);
    setIsSearching(false);
  };

  // Server search function - similar to DataDrawer's handleServerSearch
  const handleServerSearch = useCallback(async (searchTerm: string) => {
    if (serverSearchLoading) return;
    
    console.log('[MapComponent] Server search requested for:', searchTerm);
    setServerSearchLoading(true);
    
    try {
      // Find places endpoint from config (same as DataDrawer approach)
      const placesEndpoint = config?.apiEndpoints?.find(ep => ep.tableName === 'places');
      if (!placesEndpoint) {
        throw new Error('Places endpoint not found in config');
      }
      
      // Use generic adapter function instead of data source-specific logic
      const result = await searchEndpoint(placesEndpoint, searchTerm, profile, {
        fields: 'id,name,description'
      });
      
      const results = result.data;
      
      // Convert server results to SearchResult format and add to existing local results
      // Note: coordinates will be found from map layer data during zoom, not from search results
      const serverSearchResults: SearchResult[] = results.map((item: any) => ({
        id: item.id,
        name: item.name,
        type: 'place',
        layerId: placesEndpoint.mapLayerId || placesEndpoint.tableName,
        coordinates: undefined, // Will be found from map layer data during zoom
        properties: {
          description: item.description,
          fromServerSearch: true
        }
      }));
      
      // Add server results to the search results
      setSearchResults(prev => [...prev, ...serverSearchResults]);
      
      const resultText = results.length === 1 ? 'result' : 'results';
      enqueueSnackbar(
        `Found ${results.length} additional ${resultText} for "${searchTerm}"`,
        { variant: 'success', autoHideDuration: 3000 }
      );
      
    } catch (error) {
      console.error('[MapComponent] Server search error:', error);
      enqueueSnackbar(
        `Error searching server: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { variant: 'error', autoHideDuration: 5000 }
      );
    } finally {
      setServerSearchLoading(false);
    }
  }, [serverSearchLoading, config, enqueueSnackbar]);

  const handleResultSelect = async (result: SearchResult) => {
    // Set loading state
    setIsLoading(true);
    
    // Clear search after selection
    setSearchQuery("");
    
    // For coordinate searches, fly directly to the coordinates
    if (result.type === 'coordinate' && result.coordinates) {
      zoomToCoordinates(result.coordinates, mapRef, handleClick);
      setIsLoading(false);
    } 
    // For other searchable items, update SelectionContext IMMEDIATELY then zoom
    else if (result.layerId) {
      // Find the correct endpoint to get consistent entityType
      const endpoint = findEndpointByLayerId(result.layerId);
      const entityType = endpoint?.tableName || result.layerId;
      
      // Create GeoEntity from search result and update SelectionContext immediately
      const geoEntity = {
        id: result.id,
        name: result.name,
        entityType: entityType,
        layerId: result.layerId,
        coordinates: result.coordinates,
        geometry: undefined, // Will be filled in during zoom if available
        properties: {
          ...result.properties,
          id: result.id,
          name: result.name,
          description: result.properties?.description || null,
          fromSearchResult: true // Mark as coming from search to help with debugging
        }
      };
      
      // Update SelectionContext IMMEDIATELY before starting zoom
      // This ensures DataItemDetails updates instantly when search results are selected
      setSelectedEntity(geoEntity, endpoint || undefined);
      
      // Now start the zoom (which will happen in parallel with DataItemDetails loading)
      findAndZoomToPlaceById(
        result.id, 
        result.layerId, 
        layers, 
        mapRef, 
        config, 
        searchableItemsData, 
        undefined, // Don't show popup for search selections - SelectionContext already updated
        setIsLoading
      ).catch((error) => {
        console.error("Error in findAndZoomToPlaceById", error);
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  };

  // Extract searchable places from loaded map layers (similar to how DataDrawer searches loaded data)
  useEffect(() => {
    const extractSearchableData = async () => {
      if (!mapRef.current) return;

      const map = mapRef.current.getMap();
      if (!map || !map.isStyleLoaded()) return;

      // Find the places layer (which loads all places data already)
      const placesLayer = layers.find(layer => 
        layer.searchable && 
        layer.searchableFields && 
        layer.id === 'Dambi Places'
      );

      if (!placesLayer) return;

      try {
        // Query all features from the places layer
        const features = map.querySourceFeatures(placesLayer.id) || [];
        
        if (features.length > 0) {
          console.log(`[MapComponent] Extracted ${features.length} places from map layer for local search`);
          
          // Transform to searchable format using adapter system
          const searchableItems: SearchablePlaceItem[] = features.map((feature: any) => {
            // Find the endpoint for this layer to determine data source type
            const layerEndpoint = config?.apiEndpoints?.find(ep => 
              ep.mapLayerId === placesLayer.id || 
              ep.tableName.toLowerCase() === placesLayer.id.toLowerCase()
            );
            
            // Use adapter system for feature extraction
            const featureData = extractFeatureData(feature.properties, placesLayer.id, layerEndpoint);
            const featureId = featureData.id;
          
                      return {
              id: featureId,
              name: feature.properties?.name || `${placesLayer.id} Feature`,
              description: feature.properties?.description || '',
              coordinates: feature.geometry?.type === "Point" 
                ? feature.geometry.coordinates as [number, number] 
                : undefined,
              geometry: feature.geometry,
              layerId: placesLayer.id,
              properties: feature.properties || {}
            };
          });

          setSearchableItemsData({ [placesLayer.id]: searchableItems });
        }
      } catch (error) {
        console.warn('[MapComponent] Could not extract places from map layer:', error);
        // Fallback to empty search data
        setSearchableItemsData({});
      }
    };
    
    extractSearchableData();
  }, [layers, mapLibreLayers, config]);

  // Handle selected entity changes from other components (like DataDrawer)
  useEffect(() => {
    if (!selectedEntity || !mapRef.current) {
      return;
    }
    
    // Generate a unique identifier for this entity to track if we've processed it
    const entityIdentifier = `${selectedEntity.entityType}_${selectedEntity.id}`;
    
    // Create a coordinates/geometry fingerprint to detect changes within the same entity
    const coordinatesFingerprint = JSON.stringify({
      coordinates: selectedEntity.coordinates,
      geometry: selectedEntity.geometry,
      entityId: selectedEntity.id
    });
    
    // Skip if we've already processed this exact entity with the same coordinates/geometry
    if (lastProcessedEntityRef.current === entityIdentifier && 
        lastProcessedCoordinatesRef.current === coordinatesFingerprint) {
      return;
    }
    
    // Note: Popup closure is now handled automatically by the flying methods
    
    // Track entity processing to prevent duplicate operations
    
    // Update the refs with current entity and coordinates
    lastProcessedEntityRef.current = entityIdentifier;
    lastProcessedCoordinatesRef.current = coordinatesFingerprint;
    
    // Check if this entity explicitly has no coordinates by checking properties
    if (selectedEntity.properties && 
        ('hasCoordinates' in selectedEntity.properties) && 
        selectedEntity.properties.hasCoordinates === false) {
      return;
    }
    
    // If we have direct coordinates, use them for zoom
    if (selectedEntity.coordinates) {
      const [lng, lat] = selectedEntity.coordinates;
      
      // Verify coordinates are valid (not [0,0] or null/undefined)
      if (!lng && !lat) {
        return;
      }
      
      // Validate coordinate ranges
      if (typeof lng !== 'number' || typeof lat !== 'number' || 
          lng < -180 || lng > 180 || lat < -90 || lat > 90) {
        console.warn('[MAP-ZOOM] Invalid coordinates detected:', { lng, lat });
        return;
      }
      
      // Clear any existing popup before zooming to new location
      clearPopup();
      
      const map = mapRef.current.getMap();
      
      // Use centralized smart zoom logic for all entity selections
      const isFromMapClick = selectedEntity.properties?.fromMapClick;
      
      // Use centralized smart zoom with appropriate methods based on source
      smartZoomToLocation({
        center: [lng, lat],
        geometry: selectedEntity.geometry,
        map,
        duration: 2000,
        defaultZoom: 14,
        fromMapClick: isFromMapClick,
        // Provide centralized flying methods for non-map-click selections
        flyToMethod: isFromMapClick ? undefined : flyTo,
        fitBoundsMethod: isFromMapClick ? undefined : fitBounds
      });
    } 
    // If we have an ID and layer ID, use findAndZoomToPlaceById
    else if (selectedEntity.id && selectedEntity.layerId) {
      // Skip zooming for entity types that don't have map representation
      const nonMappableEntityTypes = ['media', 'persons', 'partners', 'collections', 'languages', 'plant-animals', 'special-things', 'simple-tags', 'groups'];
      
      if (nonMappableEntityTypes.includes(selectedEntity.entityType)) {
        console.log('[MAP-ZOOM] Skipping zoom for non-mappable entity type:', selectedEntity.entityType);
        return;
      }
      
      // Only clear popup for intentional navigation, not for map clicks
      if (!selectedEntity.properties?.fromMapClick) {
        clearPopup();
      }
      
      findAndZoomToPlaceById(
        selectedEntity.id, 
        selectedEntity.layerId, 
        layers, 
        mapRef, 
        config, 
        searchableItemsData, 
        profile, // Pass user profile for Fulcrum API access
        undefined, // Don't show popup for DataDrawer/DataGrid selections
        setIsLoading
      ).catch(error => {
        console.error('[MAP-ZOOM] Error zooming to selected entity:', error);
      });
    }
  }, [selectedEntity]);

  // Image handling is now done in the Map onLoad event to avoid duplicates

  // Add an effect to update terrain exaggeration when it changes in config
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !config) return;

    // Check if terrainExaggeration exists and terrain source is available
    if (config.terrainExaggeration !== undefined && terrainLayer && map.getSource(terrainLayer.id)) {
      // Set the terrain with the new exaggeration value
      map.setTerrain({
        source: terrainLayer.id,
        exaggeration: config.terrainExaggeration !== undefined 
          ? config.terrainExaggeration 
          : terrainLayer.options.exaggeration,
      });
    }
  }, [config.terrainExaggeration, terrainLayer]);

  // Handle right-click on map to show context menu
  const handleMapRightClick = (e: any) => {
    e.preventDefault();
    
    const { lngLat } = e;
    setContextMenu({
      mouseX: e.point.x,
      mouseY: e.point.y,
      coordinates: [lngLat.lng, lngLat.lat]
    });
  };

  // Close context menu
  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Copy coordinates to clipboard
  const handleCopyCoordinates = async () => {
    if (!contextMenu) return;
    
    const [lng, lat] = contextMenu.coordinates;
    const coordinatesText = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    
    try {
      await navigator.clipboard.writeText(coordinatesText);
      enqueueSnackbar(`Coordinates copied: ${coordinatesText}`, { 
        variant: "success",
        autoHideDuration: 3000 
      });
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = coordinatesText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      enqueueSnackbar(`Coordinates copied: ${coordinatesText}`, { 
        variant: "success",
        autoHideDuration: 3000 
      });
    }
    
    handleContextMenuClose();
  };

  // Add geolocation handler
  const handleLocationRequest = () => {
    if (isTrackingLocation) {
      // Stop tracking and zoom to current location
      if (locationWatchIdRef.current !== null) {
        navigator.geolocation.clearWatch(locationWatchIdRef.current);
        locationWatchIdRef.current = null;
      }
      setIsTrackingLocation(false);
      
      // Zoom to current location if we have it, then hide the dot
      if (userLocation && mapRef.current) {
        flyTo({
          center: [userLocation.longitude, userLocation.latitude],
          zoom: 16,
          duration: 2000,
          essential: true
        });
      }
      
      // Hide the blue dot after stopping tracking
      setUserLocation(null);
      
      enqueueSnackbar("Location tracking stopped", { 
        variant: "info",
        autoHideDuration: 2000 
      });
      return;
    }

    if (!navigator.geolocation) {
      enqueueSnackbar("Geolocation is not supported by this browser", { 
        variant: "error",
        autoHideDuration: 5000 
      });
      return;
    }

    setIsGettingLocation(true);

    const options = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds
      maximumAge: 30000, // 30 seconds for continuous tracking
    };

    const handleLocationSuccess = (position: GeolocationPosition, shouldZoom: boolean = false) => {
      const { latitude, longitude, accuracy } = position.coords;
      
      // Validate coordinates
      if (typeof latitude !== 'number' || typeof longitude !== 'number' || 
          latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
        enqueueSnackbar("Invalid location coordinates received", { 
          variant: "error",
          autoHideDuration: 5000 
        });
        setIsGettingLocation(false);
        return;
      }

      const isFirstLocation = !userLocation;
      setUserLocation({ latitude, longitude, accuracy });
      setIsGettingLocation(false);
      
      // Fly to user location when requested (button click) or first location
      if ((shouldZoom || isFirstLocation) && mapRef.current) {
        flyTo({
          center: [longitude, latitude],
          zoom: 16, // Close zoom level for user location
          duration: 2000,
          essential: true
        });
      }

      // Success messages removed - no need to show location found notifications
    };

    const handleLocationError = (error: GeolocationPositionError) => {
      setIsGettingLocation(false);
      setIsTrackingLocation(false);
      if (locationWatchIdRef.current !== null) {
        navigator.geolocation.clearWatch(locationWatchIdRef.current);
        locationWatchIdRef.current = null;
      }
      
      // Hide the blue dot on error
      setUserLocation(null);
      
      let errorMessage = "Unable to get your location";
      
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = "Location access was denied. Please enable location permissions.";
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = "Location information is unavailable.";
          break;
        case error.TIMEOUT:
          errorMessage = "Location request timed out. Please try again.";
          break;
        default:
          errorMessage = `Location error: ${error.message}`;
          break;
      }
      
      enqueueSnackbar(errorMessage, { 
        variant: "error",
        autoHideDuration: 5000 
      });
    };

    // First get current position, then start watching if successful
    navigator.geolocation.getCurrentPosition(
      (position) => {
        handleLocationSuccess(position, true); // Always zoom on button click
        
        // Start continuous tracking
        setIsTrackingLocation(true);
        locationWatchIdRef.current = navigator.geolocation.watchPosition(
          (position) => handleLocationSuccess(position, false), // Don't zoom during tracking updates
          handleLocationError,
          options
        );
        
        enqueueSnackbar("Started tracking your location", { 
          variant: "info",
          autoHideDuration: 3000 
        });
      },
      handleLocationError,
      options
    );
  };

  // Cleanup location tracking on unmount
  useEffect(() => {
    return () => {
      if (locationWatchIdRef.current !== null) {
        navigator.geolocation.clearWatch(locationWatchIdRef.current);
      }
      setUserLocation(null);
      setIsTrackingLocation(false);
    };
  }, []);

  return (
    <Box 
      className="mapContainer" 
      sx={{ 
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: "100%" // Ensure it fills the full container height
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          zIndex: 1000000,
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        {/* Map Controls Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            marginRight: 40, // Space between controls and search bar
          }}
        >
          <Tooltip title="Go to initial view">
            <Fab
              color="primary"
              aria-label="home"
              onClick={handleHomeClick}
              size="small"
              sx={{ marginRight: 30 }} // Space between this and next control
            >
              <HomeIcon />
            </Fab>
          </Tooltip>

          <Tooltip title="Toggle 3D view">
            <Fab
              color="primary"
              aria-label="toggle-3d"
              onClick={handle3DToggle}
              size="small"
              sx={{ marginRight: 30 }} // Space between this and next control
            >
              <ThreeDRotationIcon />
            </Fab>
          </Tooltip>

          <Tooltip title={isOrbiting ? "Stop orbiting" : "Start orbiting"}>
            <Fab
              color="primary"
              aria-label="orbit-view"
              onClick={toggleOrbit}
              size="small"
              sx={{ marginRight: 30 }} // Space between this and next control
            >
              {isOrbiting ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
            </Fab>
          </Tooltip>

          <Tooltip title={
            isTrackingLocation 
              ? "Go to location & stop tracking" 
              : "Find & track my location"
          }>
                          <Fab
                color={isTrackingLocation ? "error" : "primary"}
                aria-label="my-location"
                onClick={handleLocationRequest}
                size="small"
                disabled={isGettingLocation}
              sx={{ 
                marginRight: 30,
                ...(isGettingLocation && {
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(1)',
                    },
                    '50%': {
                      transform: 'scale(1.05)',
                    },
                    '100%': {
                      transform: 'scale(1)',
                    },
                  },
                }),
                ...(isTrackingLocation && {
                  animation: 'tracking 2s ease-in-out infinite',
                  '@keyframes tracking': {
                    '0%': {
                      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.4)',
                    },
                    '70%': {
                      boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
                    },
                    '100%': {
                      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
                    },
                  },
                })
              }}
                          >
                {isTrackingLocation ? <LocationOnIcon /> : <MyLocationIcon />}
              </Fab>
          </Tooltip>
        </Box>

        {/* Search Bar - Now inline with controls */}
        <Box sx={{ width: "300px" }}>
          <ErrorBoundary>
        <SearchBar
          value={searchQuery}
          onChange={handleSearchChange}
          onResultSelect={handleResultSelect}
          results={searchResults}
          loading={isSearching || isLoading}
          placeholder={generateSearchPlaceholder(layers)}
          showServerSearch={config.enableServerSearch ?? true}
          localResultsCount={searchResults.filter(r => !r.properties?.fromServerSearch).length}
          totalResultsCount={Object.values(searchableItemsData).reduce((total, items) => total + items.length, 0)}
          serverSearchLoading={serverSearchLoading}
          onServerSearch={async (searchTerm: string) => {
            await handleServerSearch(searchTerm);
            return []; // SearchBar expects SearchResult[], but we're handling results differently
          }}
          actions={{
            onClear: () => {
              setSearchQuery('');
              setSearchResults([]);
            }
          }}
        />
          </ErrorBoundary>
          

        </Box>
      </Box>

      {/* Data Drawer Toggle Button - Right side */}
      <ToggleButton
        isOpen={dataDrawerMode !== 'hidden'}
        onClick={handleDataDrawerToggle}
        direction="horizontal"
        position={{
          right: "16px",
          top: "50%",
          transform: "translateY(-50%)",
        }}
        zIndex={1000000}
      />

      <Map
        // Add key based on config.appTitle to force remount on config change.
        // This ensures that the Map component reads the new `initialViewState` 
        // prop when the user logs in or the configuration source changes, 
        // as it ignores updates to `initialViewState` after the initial mount.
        key={config.appTitle} 
        ref={mapRef}
        initialViewState={config.initialMapView}
        mapStyle={`https://api.maptiler.com/maps/hybrid/style.json?key=${process.env.REACT_APP_MAPTILER_KEY}`}
        attributionControl={false}
        maxPitch={65}
        reuseMaps
        onClick={(e) => {
          handleClick(e);
        }}
        onContextMenu={handleMapRightClick}
        interactiveLayerIds={getInteractiveLayerIds(layers)}
        onLoad={({ target: map }) => {
          try {
            addMissingImages(map);

            if (!map.getSource(terrainLayer.id)) {
              map.addSource(terrainLayer.id, {
                type: terrainLayer.type,
                url: terrainLayer.url,
                tileSize: terrainLayer.options.tileSize,
              });
              map.setTerrain({
                source: terrainLayer.id,
                exaggeration: config.terrainExaggeration !== undefined 
                  ? config.terrainExaggeration 
                  : terrainLayer.options.exaggeration,
              });
            }
          } catch (error) {
            console.error("Error in map onLoad", error);
          }
        }}
      >
        <Layers mapLibreLayers={mapLibreLayers} visibleLayers={visibleLayers} />
        
        {/* User Location Marker */}
        {userLocation && (
          <Marker
            longitude={userLocation.longitude}
            latitude={userLocation.latitude}
            anchor="center"
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: '#00bfff',
                border: '2px solid white',
                boxShadow: '0 2px 8px rgba(0,191,255,0.4)',
                position: 'relative',
                animation: 'bluePulse 2s ease-in-out infinite',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(0, 191, 255, 0.4)',
                  animation: 'bluePulseRing 2s ease-in-out infinite',
                },
                '@keyframes bluePulse': {
                  '0%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                    opacity: 0.8,
                  },
                  '100%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                },
                '@keyframes bluePulseRing': {
                  '0%': {
                    transform: 'translate(-50%, -50%) scale(0.8)',
                    opacity: 0.8,
                  },
                  '100%': {
                    transform: 'translate(-50%, -50%) scale(2)',
                    opacity: 0,
                  },
                },
              }}
              title={`Your location (±${Math.round(userLocation.accuracy || 0)}m)`}
            />
          </Marker>
        )}
        
        {/* React Popup for DataItemDetails */}
        {showReactPopup && popupData && (
          <Popup
            key={`popup-${popupData.entityId}`} // Force re-render when entity changes
            longitude={popupData.coordinates[0]}
            latitude={popupData.coordinates[1]}
            closeOnClick={true}
            onClose={() => {
              console.log('[MAP-POPUP-DEBUG] Popup closed via X button');
              setShowReactPopup(false);
              setPopupData(null);
            }}
            maxWidth="500px"
            className="react-popup"
            anchor="bottom"
            offset={[0, -15]}
          >
            <Box sx={{ 
              width: 'auto', // Let content dictate width
              maxWidth: '480px', // Maximum width constraint
              minWidth: '200px', // Minimum width to prevent too narrow
              maxHeight: '80vh', // Maximum height of 80% viewport height
              overflowY: 'auto',
              overflowX: 'hidden', // Hide horizontal scroll bar
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              '& .MuiCard-root': {
                height: 'auto', // Allow card to size to content
                width: '100%',
                margin: 0,
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column'
              },
              '& .react-multi-carousel-list': {
                overflow: 'visible'
              }
            }}>
              <ReactivePopupContent
                key={`popup-content-${popupData.entityId}`} // Force re-render when entity changes
                entityId={popupData.entityId}
                entityName={popupData.entityName}
                endpoint={popupData.endpoint}
                onBack={() => {
                  console.log('[MAP-POPUP-DEBUG] Back button clicked - closing popup only');
                  setShowReactPopup(false);
                  setPopupData(null);
                  // Don't clear selectedEntity - just close the popup
                }}
              />
            </Box>
          </Popup>
        )}

        {/* Traditional HTML Popup */}
        {!showReactPopup && popupContent && popupCoords && (
          <Popup
            key={`${popupCoords.lng}-${popupCoords.lat}`}
            longitude={+popupCoords.lng}
            latitude={+popupCoords.lat}
            closeOnClick={true}
            maxWidth="300px"
            className="custom-popup"
            anchor="bottom"
            offset={[0, -15]}
          >
            {HTMLReactParser(popupContent)}
          </Popup>
        )}
      </Map>

      {/* Right-click context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
            },
          },
        }}
      >
        <MenuItem onClick={handleCopyCoordinates}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            Copy Coordinates
            {contextMenu && (
              <Box component="span" sx={{ display: 'block', fontSize: '0.75rem', color: 'text.secondary' }}>
                {contextMenu.coordinates[1].toFixed(6)}, {contextMenu.coordinates[0].toFixed(6)}
              </Box>
            )}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default React.memo(MapComponent);
